import {
    Connection,
    PublicKey,
    Transaction,
    SystemProgram,
    LAMPORTS_PER_SOL,
    sendAndConfirmTransaction,
    BlockheightBasedTransactionConfirmationStrategy,
    ComputeBudgetProgram
} from '@solana/web3.js';
import {
    createTransferInstruction,
    getAssociatedTokenAddress,
    createAssociatedTokenAccountInstruction,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID
} from '@solana/spl-token';
import { restoreTradingWalletKeypair } from './tradingWalletService';

// Use the Solana mainnet RPC URL from environment variable
const RPC_URL = import.meta.env.VITE_SOLANA_RPC_URL;

// Create connection with preflight commitment level
const connection = new Connection(RPC_URL, {
    commitment: 'confirmed',
    disableRetryOnRateLimit: false,
    confirmTransactionInitialTimeout: 60000
});

// Maximum retry attempts for failed transactions
const MAX_RETRIES = 3;

// Transaction send options to maximize speed
const FAST_TX_OPTIONS = {
    skipPreflight: true,
    preflightCommitment: 'processed' as const,
    maxRetries: 3
};

// Priority fee in micro-lamports to make transactions faster
const PRIORITY_FEE = 50000; // 0.00005 SOL

export interface TransferResult {
    success: boolean;
    signature?: string;
    error?: string;
}

/**
 * Instantly transfer SOL between two trading wallets owned by the same user
 * @param ownerWalletAddress The wallet address of the owner of both trading wallets
 * @param fromWalletPublicKey The public key of the source trading wallet
 * @param toWalletPublicKey The public key of the destination trading wallet
 * @param amount Amount in SOL (not lamports)
 */
export const instantTransferSOL = async (
    ownerWalletAddress: string,
    fromWalletPublicKey: string,
    toWalletPublicKey: string,
    amount: number
): Promise<TransferResult> => {
    try {
        // Restore the keypair for the source wallet
        const sourceKeypair = await restoreTradingWalletKeypair(
            ownerWalletAddress,
            fromWalletPublicKey
        );

        if (!sourceKeypair) {
            return { success: false, error: 'Failed to recover source wallet keypair' };
        }

        // Convert amount to lamports
        const lamports = Math.floor(amount * LAMPORTS_PER_SOL);

        // Calculate optimistic balances for immediate UI update
        try {
            const fromCurrentBalance = await connection.getBalance(new PublicKey(fromWalletPublicKey));
            const toCurrentBalance = await connection.getBalance(new PublicKey(toWalletPublicKey));

            const fromOptimisticBalance = fromCurrentBalance - lamports - 5000; // Subtract amount + estimated fee
            const toOptimisticBalance = toCurrentBalance + lamports;

            // Optimistically update UI immediately
            window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                detail: {
                    address: fromWalletPublicKey,
                    balance: Math.max(0, fromOptimisticBalance) // Ensure we don't show negative balance
                }
            }));

            window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                detail: {
                    address: toWalletPublicKey,
                    balance: toOptimisticBalance
                }
            }));
        } catch (err) {
            console.error('[instantTransferSOL] Error calculating optimistic balances:', err);
            // Continue with transfer even if optimistic update fails
        }

        // Function to send transaction with retry logic
        const sendTransactionWithRetry = async (): Promise<string> => {
            let lastError;

            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    // Create transaction - recreate each retry to use fresh blockhash
                    const transaction = new Transaction();

                    // Add priority fee instruction to make transaction faster
                    transaction.add(
                        ComputeBudgetProgram.setComputeUnitPrice({
                            microLamports: PRIORITY_FEE
                        })
                    );

                    // Add transfer instruction
                    transaction.add(
                        SystemProgram.transfer({
                            fromPubkey: sourceKeypair.publicKey,
                            toPubkey: new PublicKey(toWalletPublicKey),
                            lamports
                        })
                    );

                    // Get fresh blockhash each attempt - use processed commitment for speed
                    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('processed');
                    transaction.recentBlockhash = blockhash;
                    transaction.feePayer = sourceKeypair.publicKey;

                    // Sign the transaction
                    transaction.sign(sourceKeypair);

                    // Send the transaction without waiting for confirmation - use fast options
                    const signature = await connection.sendRawTransaction(
                        transaction.serialize(),
                        FAST_TX_OPTIONS
                    );

                    console.log(`[instantTransferSOL] Transaction sent (attempt ${attempt + 1}):`, signature);

                    // Start confirmation process in background
                    confirmTransactionInBackground(signature, lastValidBlockHeight);

                    return signature;
                } catch (error) {
                    lastError = error;
                    console.warn(`[instantTransferSOL] Attempt ${attempt + 1} failed:`, error);

                    // If it's not the last attempt, wait briefly before retrying
                    if (attempt < MAX_RETRIES - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }
            }

            // If all attempts failed
            throw lastError;
        };

        // Confirm transaction in background without blocking UI
        const confirmTransactionInBackground = async (signature: string, lastValidBlockHeight: number) => {
            try {
                // Setup confirmation strategy with timeout
                const confirmationStrategy: BlockheightBasedTransactionConfirmationStrategy = {
                    signature,
                    blockhash: await connection.getLatestBlockhash('confirmed').then(res => res.blockhash),
                    lastValidBlockHeight
                };

                // Wait for confirmation
                await connection.confirmTransaction(confirmationStrategy);
                console.log('[instantTransferSOL] Transaction confirmed in background:', signature);

                // After confirmation, get the real balances
                const fromBalanceResponse = await connection.getBalance(new PublicKey(fromWalletPublicKey));
                const toBalanceResponse = await connection.getBalance(new PublicKey(toWalletPublicKey));

                console.log(`[instantTransferSOL] Source wallet confirmed balance: ${fromBalanceResponse / LAMPORTS_PER_SOL} SOL`);
                console.log(`[instantTransferSOL] Destination wallet confirmed balance: ${toBalanceResponse / LAMPORTS_PER_SOL} SOL`);

                // Update with confirmed balances
                window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                    detail: {
                        address: fromWalletPublicKey,
                        balance: fromBalanceResponse
                    }
                }));

                window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                    detail: {
                        address: toWalletPublicKey,
                        balance: toBalanceResponse
                    }
                }));
            } catch (err) {
                console.error('[instantTransferSOL] Error confirming transaction:', err);

                // Even if confirmation monitoring fails, the transaction may still succeed
                // So we still try to fetch real balances to be sure
                try {
                    const fromBalanceResponse = await connection.getBalance(new PublicKey(fromWalletPublicKey));
                    const toBalanceResponse = await connection.getBalance(new PublicKey(toWalletPublicKey));

                    window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                        detail: {
                            address: fromWalletPublicKey,
                            balance: fromBalanceResponse
                        }
                    }));

                    window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                        detail: {
                            address: toWalletPublicKey,
                            balance: toBalanceResponse
                        }
                    }));
                } catch (balanceErr) {
                    console.error('[instantTransferSOL] Error fetching final balances:', balanceErr);
                }
            }
        };

        // Send the transaction with retry logic
        const signature = await sendTransactionWithRetry();
        console.log('Instant SOL transfer initiated', signature);

        return { success: true, signature };
    } catch (error) {
        console.error('Error in instant SOL transfer:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error in instant transfer'
        };
    }
};

/**
 * Instantly transfer SPL tokens between two trading wallets owned by the same user
 * @param ownerWalletAddress The wallet address of the owner of both trading wallets
 * @param fromWalletPublicKey The public key of the source trading wallet
 * @param toWalletPublicKey The public key of the destination trading wallet
 * @param amount Amount in token units
 * @param mintAddress The token's mint address
 * @param decimals The token's decimal places
 */
export const instantTransferToken = async (
    ownerWalletAddress: string,
    fromWalletPublicKey: string,
    toWalletPublicKey: string,
    amount: number,
    mintAddress: string,
    decimals: number
): Promise<TransferResult> => {
    try {
        // Special case for SOL which is native
        if (mintAddress === 'native') {
            return instantTransferSOL(
                ownerWalletAddress,
                fromWalletPublicKey,
                toWalletPublicKey,
                amount
            );
        }

        // Restore the keypair for the source wallet
        const sourceKeypair = await restoreTradingWalletKeypair(
            ownerWalletAddress,
            fromWalletPublicKey
        );

        if (!sourceKeypair) {
            return { success: false, error: 'Failed to recover source wallet keypair' };
        }

        // Convert parameters to Solana objects
        const sourcePublicKey = sourceKeypair.publicKey;
        const destinationPublicKey = new PublicKey(toWalletPublicKey);
        const mint = new PublicKey(mintAddress);

        // Calculate amount with decimals
        const adjustedAmount = Math.floor(amount * (10 ** decimals));

        // Get associated token addresses
        const sourceATA = await getAssociatedTokenAddress(
            mint,
            sourcePublicKey,
            false,
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
        );

        const destinationATA = await getAssociatedTokenAddress(
            mint,
            destinationPublicKey,
            false,
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
        );

        // Calculate optimistic token balances for immediate UI update
        try {
            // Calculate optimistic token balances
            let fromCurrentBalance = '0';
            let toCurrentBalance = '0';

            try {
                const fromTokenInfo = await connection.getTokenAccountBalance(sourceATA);
                fromCurrentBalance = fromTokenInfo?.value?.uiAmount?.toString() || '0';
            } catch (err) {
                console.warn('[instantTransferToken] Could not fetch source token balance:', err);
            }

            try {
                const toTokenInfo = await connection.getTokenAccountBalance(destinationATA);
                toCurrentBalance = toTokenInfo?.value?.uiAmount?.toString() || '0';
            } catch (err) {
                // This is expected if destination token account doesn't exist yet
                console.warn('[instantTransferToken] Could not fetch destination token balance:', err);
            }

            // Calculate optimistic balances
            const fromOptimisticBalance = Math.max(0, parseFloat(fromCurrentBalance) - amount);
            const toOptimisticBalance = parseFloat(toCurrentBalance) + amount;

            // Optimistically update UI immediately
            window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                detail: {
                    address: fromWalletPublicKey,
                    mint: mintAddress,
                    balance: fromOptimisticBalance.toString()
                }
            }));

            window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                detail: {
                    address: toWalletPublicKey,
                    mint: mintAddress,
                    balance: toOptimisticBalance.toString()
                }
            }));
        } catch (err) {
            console.error('[instantTransferToken] Error calculating optimistic token balances:', err);
            // Continue with transfer even if optimistic update fails
        }

        // Function to send transaction with retry logic
        const sendTransactionWithRetry = async (): Promise<string> => {
            let lastError;
            let destinationAccountExists = false;

            // Check once if destination token account exists
            try {
                await connection.getAccountInfo(destinationATA);
                destinationAccountExists = true;
            } catch (error) {
                console.log('[instantTransferToken] Destination token account does not exist yet, will create it');
                // Will create it as part of transaction
            }

            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    // Create a fresh transaction for each attempt
                    const transaction = new Transaction();

                    // Add priority fee instruction to make transaction faster
                    transaction.add(
                        ComputeBudgetProgram.setComputeUnitPrice({
                            microLamports: PRIORITY_FEE
                        })
                    );

                    // Add create token account instruction if needed
                    if (!destinationAccountExists) {
                        transaction.add(
                            createAssociatedTokenAccountInstruction(
                                sourcePublicKey,
                                destinationATA,
                                destinationPublicKey,
                                mint,
                                TOKEN_PROGRAM_ID,
                                ASSOCIATED_TOKEN_PROGRAM_ID
                            )
                        );
                    }

                    // Add the transfer instruction
                    transaction.add(
                        createTransferInstruction(
                            sourceATA,
                            destinationATA,
                            sourcePublicKey,
                            adjustedAmount,
                            [],
                            TOKEN_PROGRAM_ID
                        )
                    );

                    // Get fresh blockhash each attempt
                    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('processed');
                    transaction.recentBlockhash = blockhash;
                    transaction.feePayer = sourcePublicKey;

                    // Sign the transaction
                    transaction.sign(sourceKeypair);

                    // Send the transaction without waiting for confirmation
                    const signature = await connection.sendRawTransaction(
                        transaction.serialize(),
                        FAST_TX_OPTIONS
                    );

                    console.log(`[instantTransferToken] Transaction sent (attempt ${attempt + 1}):`, signature);

                    // Start confirmation process in background
                    confirmTransactionInBackground(signature, lastValidBlockHeight);

                    return signature;
                } catch (error) {
                    lastError = error;
                    console.warn(`[instantTransferToken] Attempt ${attempt + 1} failed:`, error);

                    // If it's not the last attempt, wait briefly before retrying
                    if (attempt < MAX_RETRIES - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }
            }

            // If all attempts failed
            throw lastError;
        };

        // Confirm transaction in background without blocking UI
        const confirmTransactionInBackground = async (signature: string, lastValidBlockHeight: number) => {
            try {
                // Setup confirmation strategy with timeout
                const confirmationStrategy: BlockheightBasedTransactionConfirmationStrategy = {
                    signature,
                    blockhash: await connection.getLatestBlockhash('confirmed').then(res => res.blockhash),
                    lastValidBlockHeight
                };

                // Wait for confirmation
                await connection.confirmTransaction(confirmationStrategy);
                console.log('[instantTransferToken] Transaction confirmed in background:', signature);

                // After confirmation, get the real token balances
                const fromTokenAccount = await getAssociatedTokenAddress(
                    new PublicKey(mintAddress),
                    new PublicKey(fromWalletPublicKey)
                );

                const toTokenAccount = await getAssociatedTokenAddress(
                    new PublicKey(mintAddress),
                    new PublicKey(toWalletPublicKey)
                );

                // Fetch current token balances
                const fromTokenInfo = await connection.getTokenAccountBalance(fromTokenAccount);
                const fromBalance = fromTokenInfo?.value?.uiAmount?.toString() || '0';

                const toTokenInfo = await connection.getTokenAccountBalance(toTokenAccount);
                const toBalance = toTokenInfo?.value?.uiAmount?.toString() || '0';

                console.log(`[instantTransferToken] Source wallet confirmed token balance: ${fromBalance}`);
                console.log(`[instantTransferToken] Destination wallet confirmed token balance: ${toBalance}`);

                // Update with confirmed balances
                window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                    detail: {
                        address: fromWalletPublicKey,
                        mint: mintAddress,
                        balance: fromBalance
                    }
                }));

                window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                    detail: {
                        address: toWalletPublicKey,
                        mint: mintAddress,
                        balance: toBalance
                    }
                }));
            } catch (err) {
                console.error('[instantTransferToken] Error confirming transaction:', err);

                // Even if confirmation monitoring fails, the transaction may still succeed
                // So we still try to fetch real balances to be sure
                try {
                    const fromTokenAccount = await getAssociatedTokenAddress(
                        new PublicKey(mintAddress),
                        new PublicKey(fromWalletPublicKey)
                    );

                    const toTokenAccount = await getAssociatedTokenAddress(
                        new PublicKey(mintAddress),
                        new PublicKey(toWalletPublicKey)
                    );

                    const fromTokenInfo = await connection.getTokenAccountBalance(fromTokenAccount);
                    const fromBalance = fromTokenInfo?.value?.uiAmount?.toString() || '0';

                    const toTokenInfo = await connection.getTokenAccountBalance(toTokenAccount);
                    const toBalance = toTokenInfo?.value?.uiAmount?.toString() || '0';

                    window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                        detail: {
                            address: fromWalletPublicKey,
                            mint: mintAddress,
                            balance: fromBalance
                        }
                    }));

                    window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                        detail: {
                            address: toWalletPublicKey,
                            mint: mintAddress,
                            balance: toBalance
                        }
                    }));
                } catch (balanceErr) {
                    console.error('[instantTransferToken] Error fetching final token balances:', balanceErr);
                }
            }
        };

        // Send the transaction with retry logic
        const signature = await sendTransactionWithRetry();
        console.log('Instant token transfer initiated', signature);

        return { success: true, signature };
    } catch (error) {
        console.error('Error in instant token transfer:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error in instant transfer'
        };
    }
}; 